import {
	atomWithStorage,
	EventIdentifier,
	FeatureTeamEvent,
	TrackingEventType,
	useAnalytics,
	useLogout
} from '@capsicum/common';
import { useOnboardingAnalytics } from '@capsicum/socios-core';
import { useAtom } from 'jotai';
import { useCallback } from 'react';
import { match } from 'ts-pattern';

export enum NCOnboardingRoutesEnum {
	ADDRESS_VERIFICATION = 'ADDRESS_VERIFICATION',
	CONGRATULATIONS = 'CONGRATULATIONS',
	CREATE_YOUR_WALLET = 'CREATE_YOUR_WALLET',
	DELETE_ACCOUNT = 'DELETE_ACCOUNT',
	IDENTITY_VERIFICATION = 'IDENTITY_VERIFICATION',
	ONBOARDING_SLIDES = 'ONBOARDING_SLIDES',
	REGISTRATION = 'REGISTRATION',
	SUPPORT = 'SUPPORT'
}

type NCOnboardingRoutesTypes = keyof typeof NCOnboardingRoutesEnum;

export type NCOnboardingPageProps = {
	onBack: () => void;
	onDelete: () => void;
	onDone: () => void;
	onSuccess: () => void;
	onTellMeMore: () => void;
};

export enum NCOnboardingAnalyticsEvents {
	GO_TO_CREATE_WALLET_SCREEN = 'go_to_create_wallet_screen_from_onboarding_slides',
	GO_TO_DELETE_ACCOUNT = 'go_to_delete_account_from_support_page',
	GO_TO_NCW_FROM_CONGRATULATIONS = 'go_to_ncw_from_congratulations_screen',
	GO_TO_REGISTER_PAGE = 'go_to_ncw_registration_page',
	GO_TO_SUPPORT_PAGE = 'go_to_support_page_from_create_wallet_screen',
	LAND_ON_CONGRATULATIONS = 'ncw_registration_congratulations_page',
	LAND_ON_CREATE_WALLET_STEPS_SCREEN = 'ncw_onboarding_create_wallet_steps_information_page',
	LAND_ON_RECOVERY_KIT = 'ncw_registration_page_recovery_kit_page',
	LAND_ON_REGISTER_PAGE = 'ncw_registration_page',
	USER_DELETED = 'user_deleted_account_on_support_page'
}

const currentRouteExistingUserAtom = atomWithStorage<NCOnboardingRoutesTypes>(
	'NCWExistingUserCurrentRoute',
	NCOnboardingRoutesEnum.ONBOARDING_SLIDES
);

const currentRouteNewUserAtom = atomWithStorage<NCOnboardingRoutesTypes>(
	'NCWNewUserCurrentRoute',
	NCOnboardingRoutesEnum.REGISTRATION
);

type UseNCWOnboardingRoutesProps = {
	done?: NCOnboardingPageProps['onDone'];
	isNewUser?: boolean;
};

export const useNCWOnboardingRoutes = ({ done, isNewUser = false }: UseNCWOnboardingRoutesProps) => {
	const [currentRoute, setCurrentRoute] = useAtom(isNewUser ? currentRouteNewUserAtom : currentRouteExistingUserAtom);
	const logout = useLogout();
	const analytics = useAnalytics();
	const { sendOnboardingSuccessEvent } = useOnboardingAnalytics();

	const goToPage = useCallback(
		(page: NCOnboardingRoutesTypes) => {
			setCurrentRoute(page);
		},
		[setCurrentRoute]
	);

	const resetPage = useCallback(() => {
		const resetPage = isNewUser ? NCOnboardingRoutesEnum.REGISTRATION : NCOnboardingRoutesEnum.ONBOARDING_SLIDES;

		setCurrentRoute(resetPage);
	}, [setCurrentRoute, isNewUser]);

	const onDelete = useCallback(() => {
		logout();
		resetPage();
		done?.();
	}, [done, logout, resetPage]);

	const onDone = useCallback(() => {
		resetPage();
		done?.();
	}, [done, resetPage]);

	const triggerAnalyticsEvent = useCallback(
		(event: NCOnboardingAnalyticsEvents) => {
			match(event)
				.with(NCOnboardingAnalyticsEvents.GO_TO_CREATE_WALLET_SCREEN, () => {
					analytics.sendEvent(TrackingEventType.track, EventIdentifier.ButtonClicked, {
						feature_team: FeatureTeamEvent.ORFT,
						text: NCOnboardingAnalyticsEvents.GO_TO_CREATE_WALLET_SCREEN
					});
				})
				.with(NCOnboardingAnalyticsEvents.LAND_ON_CREATE_WALLET_STEPS_SCREEN, () => {
					analytics.sendEvent(TrackingEventType.track, EventIdentifier.PageView, {
						feature_team: FeatureTeamEvent.ORFT,
						page: NCOnboardingAnalyticsEvents.LAND_ON_CREATE_WALLET_STEPS_SCREEN
					});
				})
				.with(NCOnboardingAnalyticsEvents.GO_TO_REGISTER_PAGE, () => {
					analytics.sendEvent(TrackingEventType.track, EventIdentifier.ButtonClicked, {
						feature_team: FeatureTeamEvent.ORFT,
						text: NCOnboardingAnalyticsEvents.GO_TO_REGISTER_PAGE
					});
				})
				.with(NCOnboardingAnalyticsEvents.LAND_ON_REGISTER_PAGE, () => {
					analytics.sendEvent(TrackingEventType.track, EventIdentifier.PageView, {
						feature_team: FeatureTeamEvent.ORFT,
						page: NCOnboardingAnalyticsEvents.LAND_ON_REGISTER_PAGE
					});
				})
				.with(NCOnboardingAnalyticsEvents.GO_TO_SUPPORT_PAGE, () => {
					analytics.sendEvent(TrackingEventType.track, EventIdentifier.ButtonClicked, {
						feature_team: FeatureTeamEvent.ORFT,
						text: NCOnboardingAnalyticsEvents.GO_TO_SUPPORT_PAGE
					});
				})
				.with(NCOnboardingAnalyticsEvents.GO_TO_DELETE_ACCOUNT, () => {
					analytics.sendEvent(TrackingEventType.track, EventIdentifier.ButtonClicked, {
						feature_team: FeatureTeamEvent.ORFT,
						text: NCOnboardingAnalyticsEvents.GO_TO_DELETE_ACCOUNT
					});
				})
				.with(NCOnboardingAnalyticsEvents.USER_DELETED, () => {
					analytics.sendEvent(TrackingEventType.track, EventIdentifier.ButtonClicked, {
						feature_team: FeatureTeamEvent.ORFT,
						text: NCOnboardingAnalyticsEvents.USER_DELETED
					});
				})
				.with(NCOnboardingAnalyticsEvents.LAND_ON_RECOVERY_KIT, () => {
					analytics.sendEvent(TrackingEventType.track, EventIdentifier.PageView, {
						feature_team: FeatureTeamEvent.ORFT,
						page: NCOnboardingAnalyticsEvents.LAND_ON_RECOVERY_KIT
					});
				})
				.with(NCOnboardingAnalyticsEvents.LAND_ON_CONGRATULATIONS, () => {
					sendOnboardingSuccessEvent();

					analytics.sendEvent(TrackingEventType.track, EventIdentifier.PageView, {
						feature_team: FeatureTeamEvent.ORFT,
						page: NCOnboardingAnalyticsEvents.LAND_ON_CONGRATULATIONS
					});
				})
				.with(NCOnboardingAnalyticsEvents.GO_TO_NCW_FROM_CONGRATULATIONS, () => {
					analytics.sendEvent(TrackingEventType.track, EventIdentifier.ButtonClicked, {
						feature_team: FeatureTeamEvent.ORFT,
						text: NCOnboardingAnalyticsEvents.GO_TO_NCW_FROM_CONGRATULATIONS
					});
				});
		},
		[analytics, sendOnboardingSuccessEvent]
	);

	return {
		currentRoute,
		goToPage,
		onDelete,
		onDone,
		resetPage,
		triggerAnalyticsEvent
	};
};
